import { useCallback, useEffect, useId, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown } from 'lucide-react';
import api from '../../lib/api';
import Button from '../../ui/Button';
import Icon from '../../ui/Icon';
import Select from '../../ui/Select';
import Skeleton from '../../ui/Skeleton';
import ErrorState from '../../ui/ErrorState';
import Meter from '../../ui/Meter';
import BurnUp from '../../ui/BurnUp';
import DayBars from '../../ui/DayBars';
import SmallMultiples from '../../ui/SmallMultiples';
import ShareRing from '../../ui/ShareRing';
import Tabs from '../../ui/Tabs';
import { clean } from '../../shell/copy';
import { METRICS, METRIC_KEYS, RANGES, RANGE_KEY, SERIES_IN_FLIGHT, bucket, detailRows, namedPeople, outputScope, periodLabel, periodWord, rangeQuery, seriesQueue } from './outputModel';

export { outputScope, periodLabel, rangeQuery };

// Reports → Output (OUT-2, OUT-4; docs/OUTPUT-C5-v1.md §5 and §7, DESIGN rev 2.3). Minutes of finished video three ways:
// Scripted (writers, at script approval), Filmed (producers, when the card is marked Filmed) and Edited (editors, at Qual
// approval), each against daily targets. Every panel reads /api/output/summary, /series and /share only; the CSV is
// /api/output/export.csv; the Filmed Details' list of defaulted producers reads /api/live-production. Who sees what is
// the API's (routes/output.js scopeOf); the page shows the people each response lists and never filters them again:
//   everyone  Admin, Power User, Manager, Producer, Post Lead: every role-holder, the person filter, the share rings
//   own       Writer, Editor: own rows, plus the team totals
//   team      Qual Reviewer (and any other staff role): the team totals only
// Clients and talent never reach /reports (App.jsx, shell/nav.js), so this section makes no call for them.

export function useOutputRange(userId) {
  const [range, setRange] = useState(() => {
    try { const v = JSON.parse(localStorage.getItem(RANGE_KEY) || '{}')[userId]; return RANGES.some(([k]) => k === v) ? v : 'week'; }
    catch { return 'week'; }
  });
  const choose = useCallback(next => {
    setRange(next);
    try { const all = JSON.parse(localStorage.getItem(RANGE_KEY) || '{}'); all[userId] = next; localStorage.setItem(RANGE_KEY, JSON.stringify(all)); }
    catch { /* private mode: the choice lasts for this visit */ }
  }, [userId]);
  return [range, choose];
}

// ── Data ─────────────────────────────────────────────────────────────────────
const errStatus = e => e?.response?.status;
const SHARES = [
  ['writer', 'Scripted by writer'],
  ['producer', 'Filmed by producer'],
  ['editor', 'Edited by editor'],
  ['client', 'Edited by client'],
];

// HD-11.1 (P1): the section fills in stages, so the figures never wait on the charts.
//   1. the summary (and the chosen person's summary): the meters render as soon as it returns;
//   2. the share rings, and the team series of the chart tab on show (loadTeam: one metric, asked when its tab opens);
//   3. the per-person small multiples, each fetched when its chart scrolls into view (loadMultiple).
// Every /series call goes through one queue of at most SERIES_IN_FLIGHT calls: on live, a year's 18 per-person calls fired
// together queued on the API for about 1.2 s each and held the figures back (BUILD-REPORT-HD-11-live2.md, P1).
export function useOutput({ range, from, to, person, user }) {
  const scope = outputScope(user);
  const q = rangeQuery(range, from, to);
  const [data, setData] = useState({ state: 'loading' });
  // series by key: `team:${metric}` (the team's, or the chosen person's) and `${metric}:${id}` (one person's small
  // multiple); each is data, { failed: true }, or absent (not asked yet)
  const [series, setSeries] = useState({});
  const [defaulted, setDefaulted] = useState(null);   // the Filmed Details' list: null (not asked), 'loading', [cards] or { failed }
  const [n, setN] = useState(0);
  const reload = useCallback(() => setN(x => x + 1), []);
  // The viewer is read through a ref: a token refresh makes a new user object, which must not refetch the section.
  const uid = user?.id;
  const run = useRef({ gen: 0, queue: seriesQueue(SERIES_IN_FLIGHT), asked: new Set(), q: null, p: '' });

  useEffect(() => {
    const cur = run.current;   // one object for the hook's life (never reassigned)
    const gen = ++cur.gen;
    const p = person && scope === 'everyone' ? `&person=${person}` : '';
    cur.asked = new Set(); cur.q = q; cur.p = p;
    setSeries({});
    if (!q) { setData({ state: 'needs-dates' }); return undefined; }
    const live = () => run.current.gen === gen;
    // A new range or person clears the old figures at once: they must never show under the new range's label.
    setData(d => ({ state: 'loading', prevBase: d.base || d.prevBase }));   // prevBase: only the person filter's names
    const val = r => (r.status === 'fulfilled' ? r.value.data : { failed: true });
    (async () => {
      let base, focus = null;
      try {
        const [b, f] = await Promise.allSettled([api.get(`/api/output/summary?${q}`), p ? api.get(`/api/output/summary?${q}${p}`) : Promise.resolve({ data: null })]);
        if (b.status === 'rejected') throw b.reason;
        base = b.value.data; focus = val(f);
      } catch (e) {
        if (!live()) return;
        const s = errStatus(e);
        setData({ state: s === 403 || s === 404 ? 'refused' : s === 400 ? 'bad-range' : 'error', message: clean(e?.response?.data?.error) });
        return;
      }
      if (!live()) return;
      // One small multiple per person the API lists on each board (the chosen person only, when one is chosen). One day
      // draws no charts (the meters carry it), so it asks for no series.
      const oneDay = base.range.from === base.range.to;
      const multiples = oneDay ? [] : METRIC_KEYS.flatMap(m => namedPeople(base.metrics[m]).filter(x => !person || scope !== 'everyone' || x.teamMemberId === person)
        .map(x => ({ metric: m, id: x.teamMemberId, name: x.name, amount: x.amount, target: x.targetSoFar ?? null })));   // the table's target runs to today, as the chart's
      // (1) the meters
      setData({ state: 'data', scope, base, focus, shares: scope === 'everyone' && !oneDay ? undefined : [], multiples });
      if (oneDay || scope !== 'everyone') return;
      // (2) the share rings
      const got = await Promise.allSettled(SHARES.map(([by]) => api.get(`/api/output/share?by=${by}${by === 'client' ? '&metric=edited_min' : ''}&${q}`)));
      if (live()) setData(d => ({ ...d, shares: got.map(val) }));
    })();
    return () => { cur.gen++; };
  }, [q, person, scope, uid, n]);

  const ask = useCallback((key, url) => {
    const cur = run.current;
    if (!cur.q || cur.asked.has(key)) return;
    cur.asked.add(key);
    const gen = cur.gen;
    cur.queue(() => (run.current.gen === gen ? api.get(url(cur.q, cur.p)) : Promise.resolve({ data: null })))
      .then(r => r.data, () => ({ failed: true }))
      .then(v => { if (run.current.gen === gen && v) setSeries(s => ({ ...s, [key]: v })); });
  }, []);
  // (2) the team series of one metric (the chosen person's, when one is chosen), asked when its chart tab is on show
  const loadTeam = useCallback(metric => ask(`team:${metric}`, (q0, p0) => `/api/output/series?metric=${metric}&${q0}${p0}`), [ask]);
  // (3) a small multiple asks for its series when it scrolls into view; once per person, metric and range.
  const loadMultiple = useCallback((metric, id) => ask(`${metric}:${id}`, q0 => `/api/output/series?metric=${metric}&${q0}&person=${id}`), [ask]);

  // The Filmed Details' list: cards whose producer the API defaulted at Filmed (ruling K) and nobody has confirmed. Asked
  // once, when that Details opens; the card list carries producerDefaulted (OUT-3.1).
  const askedDefaulted = useRef(false);
  const loadDefaulted = useCallback(() => {
    if (scope !== 'everyone' || askedDefaulted.current) return;
    askedDefaulted.current = true;
    setDefaulted('loading');
    api.get('/api/live-production?includeArchived=true')
      .then(r => setDefaulted((r.data || []).filter(c => c.producerDefaulted)),
        () => { askedDefaulted.current = false; setDefaulted({ failed: true }); });
  }, [scope]);

  return { ...data, series, loadTeam, loadMultiple, defaulted, loadDefaulted, scope, reload };
}

// The person filter: everyone scope only. The names are the three boards' people (every Writer, Producer and Editor,
// ruling H), as the summary lists them.
export function personOptions(base) {
  if (!base) return [];
  const map = new Map();
  for (const m of METRIC_KEYS) for (const x of namedPeople(base.metrics[m])) if (x.name) map.set(x.teamMemberId, x.name);
  return [...map].map(([value, label]) => ({ value, label: clean(label) })).sort((a, b) => a.label.localeCompare(b.label));
}

export async function downloadOutputCsv(range, from, to) {
  const q = rangeQuery(range, from, to);
  const res = await api.get(`/api/output/export.csv?${q}`, { responseType: 'blob' });
  const name = (res.headers?.['content-disposition'] || '').match(/filename="([^"]+)"/)?.[1] || 'output.csv';
  const url = URL.createObjectURL(new Blob([res.data], { type: 'text/csv' }));
  const a = document.createElement('a');
  a.href = url; a.download = name; a.click();
  URL.revokeObjectURL(url);
}

// ── Pieces ───────────────────────────────────────────────────────────────────
const cardTitle = c => clean(c.script?.scriptName || c.notes || 'Riff card');

function Details({ children, onOpen }) {
  const [open, setOpen] = useState(false);
  const id = useId();
  return (
    <div className="ui-output__details">
      <button type="button" className="ui-disclosure ui-output__disclosure" aria-expanded={open} aria-controls={id}
        onClick={() => setOpen(o => { if (!o) onOpen?.(); return !o; })}>
        <Icon icon={ChevronDown} />Details
      </button>
      {open && <div id={id}>{children}</div>}
    </div>
  );
}

function DefaultedList({ list }) {
  if (list == null || list === 'loading') return <p className="ui-output__facts-note" aria-busy="true">Looking for defaulted producers.</p>;
  if (list.failed) return <p className="ui-output__facts-note">The defaulted producers did not load. Close Details and open it again.</p>;
  if (!list.length) return <p className="ui-output__facts-note">Every producer on a card has been set or confirmed by hand.</p>;
  return (
    <div className="ui-stack ui-stack--tight">
      <p className="ui-output__facts-note">Producer defaulted at Filmed, not yet confirmed ({list.length}):</p>
      <ul className="ui-output__cards">
        {list.slice(0, 12).map(c => (
          <li key={c.id}><Link className="ui-link" to={`/production/${c.id}`}>{cardTitle(c)}</Link><span className="ui-text-3">, {clean(c.producer?.name) || 'no name'}</span></li>
        ))}
        {list.length > 12 && <li className="ui-text-3">and {list.length - 12} more</li>}
      </ul>
    </div>
  );
}

function MetricCard({ metric, label, who, team, focus, scope, rg, focusName, out }) {
  const m = (focus || team).metrics[metric];
  const people = namedPeople(m);
  const rows = detailRows(metric, focus ? m : team.metrics[metric], scope, clean);
  return (
    <div className="ui-region ui-region--pad ui-output__meter" data-metric={metric}>
      <Meter label={label} sub={focus ? clean(focusName) : who} value={m.team.figure} target={m.team.target} pace={m.pace} unit="min"
        period={periodWord(rg)} mark={metric === 'filmed_min' ? 'filmed' : 'approved'} />
      {people.length > 0 && (
        <div className="ui-output__people" role="list" aria-label={`${label}: ${scope === 'own' ? 'you' : who}`}>
          {people.map(x => (
            <div role="listitem" key={x.teamMemberId}>
              <Meter size="small" name={scope === 'own' ? `${clean(x.name)} (you)` : clean(x.name)} value={x.amount} target={x.target} pace={m.pace} unit="min"
                aside={x.target == null && x.workingDays === 0 && x.offDays > 0 ? 'Time off' : m.team.target == null ? '' : 'No target set'} />
            </div>
          ))}
        </div>
      )}
      <Details onOpen={metric === 'filmed_min' && scope === 'everyone' ? out.loadDefaulted : undefined}>
        {rows.length > 0
          ? <dl className="ui-output__facts">{rows.map(([k, v]) => <div key={k}><dt>{k}</dt><dd className="ui-num">{v}</dd></div>)}</dl>
          : <p className="ui-output__facts-note">Nothing else to note for this range.</p>}
        {metric === 'filmed_min' && scope === 'everyone' && <DefaultedList list={out.defaulted} />}
      </Details>
    </div>
  );
}

function Failed({ what, onRetry }) {
  return (
    <div className="ui-cluster">
      <p className="ui-section__note">{what} did not load. Check your connection, then try again.</p>
      <Button variant="secondary" onClick={onRetry}>Try again</Button>
    </div>
  );
}

const CHART_TAB_KEY = 'hf-output:chart';   // the chart tab on show, per browser
function useChartTab() {
  const [tab, setTab] = useState(() => { try { const v = localStorage.getItem(CHART_TAB_KEY); return METRIC_KEYS.includes(v) ? v : METRIC_KEYS[0]; } catch { return METRIC_KEYS[0]; } });
  const choose = useCallback(v => { setTab(v); try { localStorage.setItem(CHART_TAB_KEY, v); } catch { /* the tab lasts for this visit */ } }, []);
  return [tab, choose];
}

// ── Section body ─────────────────────────────────────────────────────────────
export default function OutputBody({ out, clientFilter, person }) {
  const [tab, setTab] = useChartTab();
  const ready = out.state === 'data' && out.base && out.base.range.from !== out.base.range.to;
  const { loadTeam } = out;
  useEffect(() => { if (ready) loadTeam(tab); }, [ready, tab, loadTeam, out.base]);

  if (out.state === 'needs-dates') return <p className="ui-section__note ui-output__msg">Custom needs both dates. Choose a from and a to date in the filter row above.</p>;
  if (out.state === 'refused') return <p className="ui-section__note ui-output__msg">Output is not shown for your account. It covers the team's scripted, filmed and edited minutes.</p>;
  if (out.state === 'bad-range') return <p className="ui-section__note ui-output__msg">{out.message || 'That range cannot be shown.'} Choose another range.</p>;
  if (out.state === 'error') return <ErrorState title="Output did not load" message="Check your connection, then try again." onRetry={out.reload} />;
  if (out.state === 'loading' && !out.base) {
    return (
      <div className="ui-stack ui-stack--loose" aria-busy="true">
        <div className="ui-cols ui-cols--3">{[0, 1, 2].map(i => <Skeleton key={i} variant="block" height={200} />)}</div>
        <div className="ui-cols">{[0, 1].map(i => <Skeleton key={i} variant="block" height={220} />)}</div>
      </div>
    );
  }
  const { base, focus, scope } = out;
  const rg = base.range;
  const focusOk = focus && !focus.failed ? focus : null;
  const focusName = focusOk ? personOptions(base).find(o => o.value === person)?.label || 'one person' : null;
  // the span from the range itself (bucket's rule: more than 62 days is weekly), so it is right before any series arrives
  const span = (Date.parse(rg.to) - Date.parse(rg.from)) / 864e5 + 1 > 62 ? 'week' : 'day';
  const oneDay = rg.from === rg.to;
  const shown = focusOk || base;
  const any = METRIC_KEYS.some(k => shown.metrics[k].total !== 0 || shown.metrics[k].pending > 0);
  const [, tabLabel] = METRICS.find(([k]) => k === tab);
  const teamSeries = out.series[`team:${tab}`];
  const teamB = bucket(teamSeries?.points, rg);
  // HD-11.1: a small multiple without its series yet draws a skeleton and asks for the series when it scrolls into view
  const multiFor = metric => out.multiples.filter(x => x.metric === metric && !out.series[`${metric}:${x.id}`]?.failed)
    .map(x => { const sr = out.series[`${metric}:${x.id}`];
      const b = sr ? bucket(sr.points, rg) : null;
      return { key: x.id, name: clean(x.name), amount: x.amount, target: x.target, points: b ? b.points : null, upTo: b ? b.upTo : null, onVisible: () => out.loadMultiple(metric, x.id) }; });
  // every bucketed series of a range shares one day grid, so any loaded one gives the index of today
  const upToOf = items => (teamB.upTo >= 0 ? teamB.upTo : items.find(i => i.upTo != null)?.upTo);
  const own = scope === 'own';
  const of = focusName ? `, ${focusName}` : '';
  const hasTarget = shown.metrics[tab].team.target != null;

  return (
    <div className="ui-stack ui-stack--loose ui-output" aria-busy={out.state === 'loading' || undefined}>
      {clientFilter !== 'all' && <p className="ui-section__note">Output covers every project. The project filter does not apply to it.</p>}
      {focus?.failed && <Failed what="The figures for that person" onRetry={out.reload} />}

      <div className="ui-cols ui-cols--3 ui-output__meters">
        {METRICS.map(([key, label, who]) => (
          <MetricCard key={key} metric={key} label={label} who={who} team={base} focus={focusOk} scope={scope} rg={rg} focusName={focusName} out={out} />
        ))}
      </div>

      {!any && <p className="ui-section__note">Nothing scripted, filmed or edited in this range{focusName ? ` for ${focusName}` : ''}. Choose a longer range or an earlier date.</p>}

      {any && (oneDay
        ? <p className="ui-section__note">Charts start at two days. Choose Week or a longer range to see the days side by side.</p>
        : (
          <div className="ui-stack">
            <Tabs label="Chart metric" items={METRICS.map(([id, label]) => ({ id, label }))} value={tab} onChange={setTab} />
            <div className="ui-cols" role="tabpanel" aria-label={`${tabLabel} charts`}>
              {[0, 1].map(i => (
                <div className="ui-region ui-region--pad" key={i}>
                  {teamSeries === undefined ? <Skeleton variant="block" height={220} />
                    : teamSeries.failed ? <Failed what={`${tabLabel} by ${span}`} onRetry={out.reload} />
                    : i === 0
                      ? <BurnUp title={`${tabLabel}, running total${of}`} note={hasTarget ? 'the rule is the running target' : 'no target set'}
                        points={teamB.points} upTo={teamB.upTo} unit="min" periodLabel={span === 'week' ? 'Week of' : 'Day'} />
                      : <DayBars title={`${tabLabel} by ${span}${of}`} note={hasTarget ? `the rule is each ${span}'s target` : 'no target set'}
                        points={teamB.points} upTo={teamB.upTo} unit="min" periodLabel={span === 'week' ? 'Week of' : 'Day'} />}
                </div>
              ))}
            </div>
          </div>
        ))}

      {any && !oneDay && METRICS.map(([key, label, who]) => {
        const items = multiFor(key);
        if (!items.length) return null;
        return (
          <div className="ui-region ui-region--pad" key={key}>
            <SmallMultiples title={own ? `Your ${label.toLowerCase()} minutes by ${span}` : `${label} by ${span}, ${who}`}
              note={own ? null : `same scale, one chart per ${who.slice(0, -1)}`} items={items} upTo={upToOf(items)} unit="min" />
          </div>
        );
      })}

      {scope === 'everyone' && any && !oneDay && (
        <div className="ui-cols ui-output__rings">
          {SHARES.map(([by, title], i) => {
            const s = out.shares?.[i];
            return (
              <div className="ui-region ui-region--pad" key={by}>
                {!s ? <Skeleton variant="block" height={200} />
                  : s.failed ? <Failed what={title} onRetry={out.reload} />
                  : s.slices.length ? <ShareRing title={title} note={periodLabel(rg)} slices={s.slices.map(x => ({ ...x, name: clean(x.name) }))} unit="min" />
                    : <><h3 className="ui-chart__title">{title}</h3><p className="ui-section__note">Nothing in this range.</p></>}
              </div>
            );
          })}
        </div>
      )}

      <p className="ui-section__note">
        A script's minutes are the writer's own figure when they give one, otherwise its spoken words at 250 a minute, as it
        stood when the script was approved. Scripted credits the writer at approval; Filmed credits the card's producer when
        the card is marked Filmed; Edited credits the editor when the video is approved in Qual, at the video's real length.
        The team figure counts named people only; Unassigned is under Details. Time off removes the day from that person's
        target. Today's figures move as the work happens; earlier days are the nightly totals.
      </p>
    </div>
  );
}

// The filter row additions (the page's existing row, extended): the range segment and, for everyone scope, the person.
export function OutputFilters({ range, setRange, person, setPerson, options, scope }) {
  return (
    <>
      <div className="ui-seg" role="group" aria-label="Output range">
        {RANGES.map(([k, label]) => <Button key={k} variant="quiet" aria-pressed={range === k} onClick={() => setRange(k)}>{label}</Button>)}
      </div>
      {scope === 'everyone' && (
        <Select aria-label="Person" value={person} onChange={e => setPerson(e.target.value)}
          options={[{ value: '', label: 'Everyone' }, ...options]} />
      )}
    </>
  );
}
